// ─── Discussions : accès Firestore (Core) ─────────────────────────
// Les DÉCISIONS (qui voit quoi, qui administre) vivent dans
// `cibleDiscussion.js` ; les CHEMINS dans `cheminDiscussion.js`. Ici, les E/S.
//
//   calendriers/{calId}/discussions/{discId}
//   espaces/{espId}/discussions/{discId}
//
// Le porteur est dans le CHEMIN, jamais dans le document : c'est la seule
// forme que Firestore sait évaluer pour une règle de LISTE (incident du 09/08,
// détaillé dans `cheminDiscussion.js`). Chaque discussion renvoyée porte donc
// une propriété `cle` AJOUTÉE À LA VOLÉE — elle vient du chemin, elle n'est pas
// stockée : une copie finirait par diverger de sa source.
//
// ⚠️ DEUX REQUÊTES par porteur, jamais une seule : une requête LISTE échoue
// ENTIÈREMENT si un document matché est illisible. En ne matchant que du
// lisible — les « visibles à tous », puis celles où je suis dans `lecteurs` —
// aucune ne peut échouer, et le titre d'une restreinte ne sort pas de la base.
import {
  collection, doc, addDoc, setDoc, updateDoc, deleteDoc, getDoc, getDocs,
  onSnapshot, query, where, writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  cheminDiscussions, cheminDiscussion, cheminMessages, idGeneraleDe,
} from './cheminDiscussion';
import {
  modeleGenerale, generaleObligatoire, participantsEffectifs, lecteursDe,
  trierDiscussions, transitionValide,
} from './cibleDiscussion';

const colDisc = (cle) => {
  const c = cheminDiscussions(cle);
  return c ? collection(db, ...c) : null;
};
const refDisc = (cle, id) => {
  const c = cheminDiscussion(cle, id);
  return c ? doc(db, ...c) : null;
};

function fusion(lots, cle, uid, callback) {
  const vues = new Map();
  for (const lot of lots.values()) for (const d of lot) vues.set(d.id, d);
  callback(trierDiscussions(Array.from(vues.values()), uid));
}

// `onErreur` est OBLIGATOIREMENT remonté : sans lui, un refus de règle donnait
// le même écran qu'une absence de discussion — c'est ce qui a rendu l'incident
// du 09/08 indiagnosticable depuis l'app.
export function ecouterDiscussions(cle, uid, callback, onErreur) {
  const col = colDisc(cle);
  if (!col || !uid) { callback([]); return () => {}; }
  const lots = new Map([['tous', []], ['moi', []]]);
  const pousser = (k) => (snap) => {
    lots.set(k, snap.docs.map(d => ({ id: d.id, cle, ...d.data() })));
    fusion(lots, cle, uid, callback);
  };
  // On vide CE lot (l'autre écoute peut fonctionner : la liste reste partielle
  // plutôt que fausse) ET on remonte la cause.
  const echec = (k) => (e) => {
    lots.set(k, []);
    fusion(lots, cle, uid, callback);
    onErreur?.(e?.message || String(e));
  };
  const u1 = onSnapshot(query(col, where('visibleATous', '==', true)), pousser('tous'), echec('tous'));
  const u2 = onSnapshot(query(col, where('lecteurs', 'array-contains', uid)), pousser('moi'), echec('moi'));
  return () => { u1(); u2(); };
}

// Agglomérateur : plusieurs porteurs à la fois. Union d'écoutes — on n'invente
// pas une requête transverse, elle échouerait sur le premier porteur non vu.
export function ecouterDiscussionsMulti(cles, uid, callback, onErreur) {
  const parCle = new Map();
  const offs = (cles || []).map(cle =>
    ecouterDiscussions(cle, uid, (liste) => {
      parCle.set(cle, liste);
      callback(trierDiscussions(Array.from(parCle.values()).flat(), uid));
    }, onErreur),
  );
  return () => offs.forEach(off => off());
}

// Crée la Générale si elle manque. Id DÉTERMINISTE : deux appareils qui la
// créent au même instant écrivent le MÊME document.
// ⚠️ RATTRAPAGE, pas garantie : la Générale doit être posée à la création du
// porteur. `throwSiEchec` permet à l'appelant de connaître la cause — une
// création concurrente reste un cas NORMAL, donc on ne lève pas par défaut.
export async function assurerGenerale(cle, { titre, uid, nom, throwSiEchec = false } = {}) {
  if (!generaleObligatoire(cle)) return null;
  const id = idGeneraleDe(cle);
  const ref = refDisc(cle, id);
  if (!ref) return null;
  try { if ((await getDoc(ref)).exists()) return id; } catch { /* on tente la création */ }
  const modele = modeleGenerale(cle, { titre }) || {};
  try {
    await setDoc(ref, {
      titre: modele.titre || 'Général', generale: true, visibleATous: true,
      participants: [], lecteurs: [], ferme: false,
      creePar: uid || null, creeParNom: nom || '', creeLe: new Date().toISOString(),
    });
  } catch (e) {
    try { if ((await getDoc(ref)).exists()) return id; } catch { /* échec réel */ }
    if (throwSiEchec) throw e;
    return null;
  }
  return id;
}

export async function creerDiscussion({ cle, titre, visibleATous, participants, uid, nom }) {
  const col = colDisc(cle);
  if (!col || !uid) return null;
  const base = { visibleATous: !!visibleATous, participants, creePar: uid };
  const liste = participantsEffectifs(base);
  const ref = await addDoc(col, {
    titre: (titre || '').trim() || 'Discussion',
    generale: false,
    visibleATous: !!visibleATous,
    participants: liste,
    lecteurs: lecteursDe({ ...base, participants: liste }),
    ferme: false,
    creePar: uid, creeParNom: nom || '', creeLe: new Date().toISOString(),
  });
  return ref.id;
}

// `lecteurs` est TOUJOURS recalculé depuis la sélection : le laisser diverger
// couperait la requête d'un membre sans que rien ne le signale.
export async function majDiscussion(disc, champs) {
  const ref = refDisc(disc?.cle, disc?.id);
  if (!ref || !champs) return;
  const apres = { ...disc, ...champs };
  if (!transitionValide(disc, apres)) return;
  const participants = participantsEffectifs(apres);
  await updateDoc(ref, { ...champs, participants, lecteurs: lecteursDe({ ...apres, participants }) });
}

// Fermer est SANS RETOUR : pas de `rouvrir`.
export async function fermerDiscussion(disc, uid) {
  const ref = refDisc(disc?.cle, disc?.id);
  if (!ref) return;
  await updateDoc(ref, { ferme: true, fermeeLe: new Date().toISOString(), fermeePar: uid || null });
}

// Supprime une discussion ET ses messages. Le conteneur part EN DERNIER : si un
// lot échoue, la discussion reste visible et l'opération se relance — des
// messages sans conteneur seraient, eux, invisibles et irrécupérables.
export async function supprimerDiscussion(disc) {
  const ref = refDisc(disc?.cle, disc?.id);
  const chemin = cheminMessages(disc?.cle, disc?.id);
  if (!ref) return;
  if (chemin) {
    try {
      const snap = await getDocs(collection(db, ...chemin));
      const LOT = 450;                      // writeBatch plafonne à 500
      for (let i = 0; i < snap.docs.length; i += LOT) {
        const batch = writeBatch(db);
        snap.docs.slice(i, i + LOT).forEach(m => batch.delete(m.ref));
        await batch.commit();
      }
    } catch { /* messages illisibles : on supprime au moins le conteneur */ }
  }
  await deleteDoc(ref);
}

export { idGeneraleDe };
